import React, { FC } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';

import { CosmosComponent } from '../../types';

type TextElement = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4';

type TextVariants = 'small' | 'medium' | 'big';

export interface TextProps extends CosmosComponent {
  bold?: boolean;
  component?: TextElement;
  text?: JSX.Element | string;
  variant?: TextVariants;
  title?: string;
  textColor?: string;
}

export const Text: FC<TextProps> = ({
  bold,
  children,
  className,
  component = 'span',
  text,
  variant = 'medium',
  title,
  testId,
  textColor,
}) => {
  const isText = component === 'span' || component === 'p';
  const isVariant = isText && !!variant;

  return (
    <StyledText
      className={className}
      as={component}
      isText={isText}
      component={component}
      isVariant={isVariant}
      variant={variant}
      bold={bold}
      title={title}
      textColor={textColor}
      data-test-id={testId}
    >
      {text || children}
    </StyledText>
  );
};

const cssSmall = css`
  color: #333;
  font-size: 12px;
`;

const cssMedium = css`
  font-size: 14px;
`;

const cssBig = css`
  font-size: 16px;
`;

const cssH1 = css`
  margin: 40px 0;
  font-size: 24px;
  font-weight: 600;
`;

const cssH2 = css`
  margin: 10px 0;
  font-size: 20px;
  font-weight: 300;
`;

const cssH3 = css`
  margin: 5px 0;
  font-size: 14px;
`;

const getVariantCss = (variant: TextVariants): FlattenSimpleInterpolation => {
  const variantStyles: Record<TextVariants, FlattenSimpleInterpolation> = {
    small: cssSmall,
    medium: cssMedium,
    big: cssBig,
  };

  return variantStyles[variant] || [];
};

const getHeadingCss = (
  component: TextElement,
  bold = false,
): FlattenSimpleInterpolation => {
  const headingStyles: Record<string, FlattenSimpleInterpolation> = {
    h1: cssH1,
    h2: cssH2,
    h3: cssH3,
  };

  const cssBold =
    bold &&
    css`
      font-weight: 600;
    `;

  return [...(headingStyles[component] || []), ...(cssBold || [])];
};

type StyledTextProps = {
  isText: boolean;
  component: TextElement;
  isVariant: boolean;
  variant: TextVariants;
  bold?: boolean;
  textColor?: string;
};

const StyledText = styled.div<StyledTextProps>`
  font-family: 'Open Sans', sans-serif;
  color: ${({ textColor, theme: { colors } }) => colors?.[textColor]};

  ${({ isVariant, variant }) => isVariant && getVariantCss(variant)}

  ${({ isText, component, bold }) =>
    isText
      ? bold &&
        css`
          font-weight: bold;
        `
      : getHeadingCss(component, bold)}
`;
